import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import { toApiDate } from "../hubFormat";
import type {
	CreateCandidateRequest,
	HubAccess,
	HubCandidateFilters,
	HubCandidateProfile,
	HubCandidateRow,
	HubCandidatesResponse,
	HubDocType,
	HubTabAccess,
	InterviewShift,
	ProcessNoteRequest,
	UpdateCandidateRequest,
	UpdateInterviewRequest,
	UpdatePipelineStatusRequest,
	UpdateProcessRequest,
} from "../types";
import { CandidatePipelineStatus } from "../types";

export const HUB_CANDIDATES_KEY = "hubCandidates";
export const HUB_INTERVIEW_SCHEDULE_KEY = "hubInterviewSchedule";
const HUB_CANDIDATE_KEY = "hubCandidate";
const HUB_INTERVIEWERS_KEY = "hubInterviewers";
const HUB_INTERVIEWER_DEPARTMENTS_KEY = "hubInterviewerDepartments";
const HUB_ALL_INTERVIEWERS_KEY = "hubAllInterviewers";
const HUB_INTERVIEWER_CANDIDATES_KEY = "hubInterviewerCandidates";

const candidatesKey = (filters: HubCandidateFilters) => [HUB_CANDIDATES_KEY, filters] as const;
const candidateKey = (candidateId: string) => [HUB_CANDIDATE_KEY, candidateId] as const;
const interviewersKey = (departmentId: string) => [HUB_INTERVIEWERS_KEY, departmentId] as const;
const interviewerCandidatesKey = (departmentId: string | null, q: string) => [HUB_INTERVIEWER_CANDIDATES_KEY, departmentId, q] as const;

/**
 * How long an Add-Interviewer candidate page stays fresh. Sized to cover ONE modal session — open, type,
 * backspace, switch department, submit — so each distinct `q` is fetched once instead of on every remount.
 * Deliberately shorter than the 5 minutes the small option lists in this file use: this payload is ~100x
 * bigger, and the pool moves from OUTSIDE the tab (an HRIS sync filling in a phone number, an admin adding a
 * department member), which no in-app invalidation can catch. Note the pool is NOT dropped when the modal
 * closes: the modal is rendered unconditionally by the Hiring page, so this hook never unmounts, `gcTime`
 * never fires, and the response stays cached for the whole page session (reused on reopen within the window).
 */
const INTERVIEWER_CANDIDATES_STALE_TIME_MS = 60_000;

/**
 * How long a loaded candidates grid stays fresh. The response is the whole filtered set, so a remount
 * (sub-tab switch, chat-dock toggle) re-fetching it is not free; 60s covers a normal browse without holding
 * rows past the point a recruiter would notice them going stale. Correctness never depends on this window —
 * every write in this file invalidates the `HUB_CANDIDATES_KEY` prefix explicitly.
 */
const CANDIDATES_STALE_TIME_MS = 60_000;

/**
 * The caller's hiring-role summary — gates the recruiter-only Hub surfaces (Interviewers tab + interviewer
 * inbox). Cached for the session; the candidate/chat scoping itself is enforced server-side regardless.
 */
export const useHubAccessQuery = () => {
	return useQuery<HubAccess>({
		queryKey: ["hubAccess"] as const,
		queryFn: async () => (await backendService.fetchHubAccess()).data,
		staleTime: 5 * 60 * 1000,
	});
};

/**
 * Whether the caller gets the Hiring tab (a hiring role: is_recruiter / is_interviewer / support) — drives
 * the nav + route gate. Un-gated, so it never 403s.
 */
/** Distinct positions in scope — the Position single-select options (Add/Edit candidate). Fetched lazily. */
export const useHubPositionsQuery = (enabled: boolean) => {
	const { trackError } = useAnalytics();
	return useQuery({
		queryKey: ["hubPositions"] as const,
		queryFn: async () => (await backendService.fetchHubPositions()).data,
		enabled,
		staleTime: 5 * 60 * 1000,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_positions_fetch_error", { error_message: (error as Error)?.message });
			return false;
		},
	});
};

export const useHubTabAccessQuery = (enabled: boolean) => {
	return useQuery<HubTabAccess>({
		queryKey: ["hubTabAccess"] as const,
		queryFn: async () => (await backendService.fetchHubTabAccess()).data,
		enabled,
		staleTime: 5 * 60 * 1000,
	});
};

/** Every dated interview in the caller's scope, for the Interview Schedule calendar (fetched lazily). */
export const useInterviewScheduleQuery = (enabled: boolean) => {
	const { trackError } = useAnalytics();
	return useQuery({
		queryKey: [HUB_INTERVIEW_SCHEDULE_KEY] as const,
		queryFn: async () => (await backendService.fetchInterviewSchedule()).data.entries,
		enabled,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_interview_schedule_fetch_error", { error_message: (error as Error)?.message });
			return false;
		},
	});
};

/** Pull the UIVisibleError i18n key off an axios error, so a backend refusal surfaces the real reason. */
const hubErrorKey = (error: unknown): string | null => {
	const detail = (error as AxiosError<{ detail?: { message?: string } | string }>)?.response?.data?.detail;
	if (detail && typeof detail === "object" && detail.message) return detail.message;
	return null;
};

/** Invalidate every cached candidate grid page (the pipeline count bar is derived from the loaded rows). */
const invalidateGrid = (queryClient: ReturnType<typeof useQueryClient>) => {
	void queryClient.invalidateQueries({ queryKey: [HUB_CANDIDATES_KEY] });
};

/**
 * Everything that reflects a person's interviewer role after an add/remove: the roster cards + department
 * dropdown, the Edit modal's editable-departments list (so reopening shows the just-saved checkboxes, not
 * stale ones) and its removal-warning assignment caches, and the candidate grid (a revoke unassigns
 * upcoming interviews, so the grid's interviewer cells must refresh). Keyed to the affected user.
 */
const invalidateInterviewerViews = (queryClient: ReturnType<typeof useQueryClient>, userId: string) => {
	// The Add-Interviewer pool is defined by who is NOT yet an interviewer, so every grant/revoke changes it.
	// A prefix invalidation, because the pool is cached per (departmentId, q) and a role change moves the
	// person in or out of ALL of those pages. Required by INTERVIEWER_CANDIDATES_STALE_TIME_MS — without it a just-granted
	// person lingers in the picker, and a just-revoked one stays missing, for the whole stale window.
	void queryClient.invalidateQueries({ queryKey: [HUB_INTERVIEWER_CANDIDATES_KEY] });
	void queryClient.invalidateQueries({ queryKey: [HUB_INTERVIEWER_DEPARTMENTS_KEY] });
	void queryClient.invalidateQueries({ queryKey: [HUB_ALL_INTERVIEWERS_KEY] });
	void queryClient.invalidateQueries({ queryKey: ["hubEditableInterviewerDepartments", userId] });
	void queryClient.invalidateQueries({ queryKey: ["hubInterviewerAssignments"] });
	void queryClient.invalidateQueries({ queryKey: ["hubAllInterviewerAssignments", userId] });
	invalidateGrid(queryClient);
};

// --------------------------------------------------------------------------- //
// Reads
// --------------------------------------------------------------------------- //
/**
 * The candidates grid's rows: the COMPLETE filtered set from `GET /candidates`, in one request.
 *
 * The endpoint is uncapped and unpaged by design. It has one caller, and any `limit` would be a cap that
 * hides rows the caller is entitled to see — which breaks four things here silently, because each is computed
 * over "what got loaded": the Status / Position / Interviewer / applied-date filters run client-side
 * (`filterHubRows`), the Position and Interviewer dropdown OPTIONS are derived from the loaded rows, the
 * pipeline count bar is computed from them (there is no server counts endpoint), and `SystemDataGrid` sorts
 * client-side. Do NOT reintroduce a row limit or a page size here.
 *
 * Consuming `signal` turns on real cancellation for this query for the first time (TanStack v5 only aborts a
 * fetch whose `queryFn` touched `signal`). One consequence worth knowing: `useSetRowInterviewer` cancels this
 * prefix in `onMutate` and invalidates it in `onSettled`, so rapid inline interviewer assignments now
 * genuinely abort and restart the whole load each time. The optimistic `setQueriesData` keeps the visible
 * rows correct throughout, so that is extra request volume, not a correctness problem.
 */
export const useHubCandidatesQuery = (filters: HubCandidateFilters) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: candidatesKey(filters),
		queryFn: async ({ signal }) => (await backendService.fetchHubCandidates(filters, signal)).data,
		// Keep the previously loaded rows visible while the next load runs, so filtering doesn't flash empty.
		placeholderData: keepPreviousData,
		staleTime: CANDIDATES_STALE_TIME_MS,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_candidates_fetch_error", { error_message: (error as Error)?.message });
			showSnackbar(t("hiring_hub.error.candidates"), "error");
			return false;
		},
	});
};

export const useHubCandidateQuery = (candidateId: string | null) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: candidateKey(candidateId ?? ""),
		queryFn: async () => (await backendService.fetchHubCandidate(candidateId as string)).data,
		enabled: !!candidateId,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_candidate_fetch_error", { error_message: (error as Error)?.message, candidate_id: candidateId });
			showSnackbar(t("hiring_hub.error.candidate"), "error");
			return false;
		},
	});
};

/** Per-department interviewer options, fetched lazily (enable only when a row's dropdown opens). */
export const useHubInterviewersQuery = (departmentId: string | null, enabled: boolean) => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: interviewersKey(departmentId ?? ""),
		queryFn: async () => (await backendService.fetchHubInterviewers(departmentId as string)).data.users,
		enabled: enabled && !!departmentId,
		// The department's interviewer roster barely changes within a session — cache it so reopening a
		// dropdown is instant.
		staleTime: 5 * 60 * 1000,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_interviewers_fetch_error", { error_message: (error as Error)?.message, department_id: departmentId });
			return false;
		},
	});
};

/**
 * The departments (with ≥1 interviewer) the caller manages, for the Interviewers-tab department selector.
 * Returns only departments that actually have interviewers to show, so the dropdown never offers an empty
 * roster; `[]` when the caller has none.
 */
export const useInterviewerDepartmentsQuery = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: [HUB_INTERVIEWER_DEPARTMENTS_KEY] as const,
		queryFn: async () => (await backendService.fetchInterviewerDepartments()).data,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_interviewer_departments_fetch_error", { error_message: (error as Error)?.message });
			showSnackbar(t("hiring_hub.error.interviewer_departments"), "error");
			return false;
		},
	});
};

/** The full interviewer roster across the caller's visible departments (Interviewers tab "All departments"). */
export const useAllInterviewersQuery = () => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: [HUB_ALL_INTERVIEWERS_KEY] as const,
		queryFn: async () => (await backendService.fetchAllInterviewers()).data.interviewers,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_all_interviewers_fetch_error", { error_message: (error as Error)?.message });
			return false;
		},
	});
};

/**
 * The candidates an interviewer is currently assigned to in a department — fetched lazily (enable only when
 * the delete-confirmation dialog opens) so the recruiter can see who becomes unassigned before revoking.
 */
export const useInterviewerAssignmentsQuery = (departmentId: string | null, userId: string | null, enabled: boolean) => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: ["hubInterviewerAssignments", departmentId ?? "", userId ?? ""] as const,
		queryFn: async () => (await backendService.fetchInterviewerAssignments(departmentId as string, userId as string)).data.assignments,
		enabled: enabled && !!departmentId && !!userId,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_interviewer_assignments_fetch_error", { error_message: (error as Error)?.message, department_id: departmentId, user_id: userId });
			return false;
		},
	});
};

/**
 * Every candidate an interviewer is assigned to across all their departments — feeds the whole-interviewer
 * removal confirmation (who becomes unassigned everywhere). Fetched lazily while the dialog is open.
 */
export const useAllInterviewerAssignmentsQuery = (userId: string | null, enabled: boolean) => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: ["hubAllInterviewerAssignments", userId ?? ""] as const,
		queryFn: async () => (await backendService.fetchAllInterviewerAssignments(userId as string)).data.assignments,
		enabled: enabled && !!userId,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_all_interviewer_assignments_fetch_error", { error_message: (error as Error)?.message, user_id: userId });
			return false;
		},
	});
};

/**
 * The departments the caller may add/remove a person's interviewer role in (their manageable memberships),
 * each with the current is_interviewer flag — fetched lazily (enable only when the Edit modal opens).
 */
export const useEditableInterviewerDepartmentsQuery = (userId: string | null, enabled: boolean) => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: ["hubEditableInterviewerDepartments", userId ?? ""] as const,
		queryFn: async () => (await backendService.fetchEditableInterviewerDepartments(userId as string)).data.departments,
		enabled: enabled && !!userId,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_editable_interviewer_departments_fetch_error", { error_message: (error as Error)?.message, user_id: userId });
			return false;
		},
	});
};

/**
 * The caller's is_recruiter departments as {id, display_name} options — the departments they may create
 * candidates / flag interviewers in. Drives both the Add Interviewer department-first flow and the Add
 * Candidate department picker. `[]` when the caller recruits nowhere.
 */
export const useInterviewerDepartmentOptionsQuery = (enabled: boolean) => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: ["hubInterviewerDepartmentOptions"] as const,
		queryFn: async () => (await backendService.fetchInterviewerDepartmentOptions()).data,
		enabled,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_interviewer_department_options_fetch_error", { error_message: (error as Error)?.message });
			return false;
		},
	});
};

/** Departments the caller may CREATE a candidate in (recruiter ∪ interviewer departments) — drives the Add
 *  Candidate modal's department picker, so interviewer-only users can create in their own departments. */
export const useCandidateDepartmentOptionsQuery = (enabled: boolean) => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: ["hubCandidateDepartmentOptions"] as const,
		queryFn: async () => (await backendService.fetchCandidateDepartmentOptions()).data,
		enabled,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_candidate_department_options_fetch_error", { error_message: (error as Error)?.message });
			return false;
		},
	});
};

/**
 * Typeahead search for the Add Interviewer picker: existing department members whose name matches `q`.
 * Scoped to `departmentId` when set, otherwise across the recruiter's manageable departments. Debounce `q`
 * at the call site; `enabled` gates the request. An empty `q` with no department returns the caller's WHOLE
 * grantable pool — which is why the picker is already populated before anyone types.
 */
export const useInterviewerCandidatesQuery = (departmentId: string | undefined, q: string, enabled: boolean) => {
	const { trackError } = useAnalytics();

	return useQuery({
		queryKey: interviewerCandidatesKey(departmentId ?? null, q),
		queryFn: async () => (await backendService.searchInterviewerCandidates(departmentId ?? null, q)).data,
		enabled,
		staleTime: INTERVIEWER_CANDIDATES_STALE_TIME_MS,
		// Keep the previous matches visible while the next keystroke's results load, so the list doesn't flash.
		placeholderData: keepPreviousData,
		throwOnError: (error: unknown) => {
			trackError("hiring_hub_interviewer_candidates_fetch_error", { error_message: (error as Error)?.message, department_id: departmentId });
			return false;
		},
	});
};

// --------------------------------------------------------------------------- //
// Candidate / process / interview writes
// --------------------------------------------------------------------------- //
export const useCreateHubCandidate = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (body: CreateCandidateRequest) => (await backendService.createHubCandidate(body)).data,
		throwOnError: false,
		onMutate: (body) => {
			track("hiring_hub_candidate_create_start", { department_id: body.departmentId });
		},
		onSuccess: (profile: HubCandidateProfile) => {
			track("hiring_hub_candidate_create_success", { candidate_id: profile.id });
			// Seed the profile cache from the POST response so opening the new candidate is instant.
			queryClient.setQueryData<HubCandidateProfile>(candidateKey(profile.id), profile);
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.add_candidate.created"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_candidate_create_error", { error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.create_candidate"), "error");
		},
	});
};

/** Hard-delete a candidate (and all their data). Invalidates the grid on success. */
export const useDeleteHubCandidate = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ candidateId, notify = false }: { candidateId: string; notify?: boolean }) => {
			await backendService.deleteHubCandidate(candidateId, notify);
		},
		throwOnError: false,
		onSuccess: (_data, { candidateId }) => {
			track("hiring_hub_candidate_delete_success", { candidate_id: candidateId });
			void queryClient.removeQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.row_menu.delete_candidate_success"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_candidate_delete_error", { error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

export const useUpdateHubCandidate = (candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (body: UpdateCandidateRequest) => {
			await backendService.updateHubCandidate(candidateId, body);
		},
		throwOnError: false,
		onMutate: () => {
			track("hiring_hub_candidate_update_start", { candidate_id: candidateId });
		},
		onSuccess: () => {
			track("hiring_hub_candidate_update_success", { candidate_id: candidateId });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.profile.saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_candidate_update_error", { candidate_id: candidateId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

export const useUpdateHubProcess = (processId: string, candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (body: UpdateProcessRequest) => {
			await backendService.updateHubProcess(processId, body);
		},
		throwOnError: false,
		onMutate: () => {
			track("hiring_hub_process_update_start", { process_id: processId });
		},
		onSuccess: () => {
			track("hiring_hub_process_update_success", { process_id: processId });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.profile.saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_process_update_error", { process_id: processId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

/**
 * The candidate-profile note mutations (add / edit / delete). Notes live only on the profile — they are not a
 * grid column — so each one invalidates just the candidate profile, never the grid. `hiring_chat.note.not_found`
 * (404) surfaces when someone else deleted the note first; `hubErrorKey` maps it to its translated message.
 */
export const useCreateProcessNote = (processId: string, candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (body: ProcessNoteRequest) => {
			await backendService.createHubProcessNote(processId, body);
		},
		throwOnError: false,
		onMutate: () => {
			track("hiring_hub_note_create_start", { process_id: processId });
		},
		onSuccess: () => {
			track("hiring_hub_note_create_success", { process_id: processId });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			showSnackbar(t("hiring_hub.profile.note_saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_note_create_error", { process_id: processId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

export const useUpdateProcessNote = (noteId: string, candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (body: ProcessNoteRequest) => {
			await backendService.updateHubProcessNote(noteId, body);
		},
		throwOnError: false,
		onMutate: () => {
			track("hiring_hub_note_update_start", { note_id: noteId });
		},
		onSuccess: () => {
			track("hiring_hub_note_update_success", { note_id: noteId });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			showSnackbar(t("hiring_hub.profile.note_saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_note_update_error", { note_id: noteId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

export const useDeleteProcessNote = (noteId: string, candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async () => {
			await backendService.deleteHubProcessNote(noteId);
		},
		throwOnError: false,
		onMutate: () => {
			track("hiring_hub_note_delete_start", { note_id: noteId });
		},
		onSuccess: () => {
			track("hiring_hub_note_delete_success", { note_id: noteId });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			showSnackbar(t("hiring_hub.profile.note_deleted"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_note_delete_error", { note_id: noteId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

export const useSetHubPipelineStatus = (processId: string, candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (body: UpdatePipelineStatusRequest) => {
			await backendService.setHubPipelineStatus(processId, body);
		},
		throwOnError: false,
		onMutate: (body) => {
			track("hiring_hub_pipeline_status_set_start", { process_id: processId, pipeline_status: body.pipelineStatus });
		},
		onSuccess: (_data, body) => {
			track("hiring_hub_pipeline_status_set_success", { process_id: processId, pipeline_status: body.pipelineStatus });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.pipeline_status.saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_pipeline_status_set_error", { process_id: processId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

export const useCreateHubInterview = (processId: string, candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (body: UpdateInterviewRequest) => (await backendService.createHubInterview(processId, body)).data,
		throwOnError: false,
		onMutate: () => {
			track("hiring_hub_interview_create_start", { process_id: processId });
		},
		onSuccess: () => {
			track("hiring_hub_interview_create_success", { process_id: processId });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.interview.saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_interview_create_error", { process_id: processId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

export const useUpdateHubInterview = (processId: string, candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ interviewId, body }: { interviewId: string; body: UpdateInterviewRequest }) => {
			await backendService.updateHubInterview(processId, interviewId, body);
		},
		throwOnError: false,
		onMutate: () => {
			track("hiring_hub_interview_update_start", { process_id: processId });
		},
		onSuccess: () => {
			track("hiring_hub_interview_update_success", { process_id: processId });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.interview.saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_interview_update_error", { process_id: processId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

/**
 * The inline grid interviewer dropdown: PATCH one row's most-recent interview's interviewer, applied
 * optimistically to every cached candidate page so the cell updates the instant you pick (rolled back on
 * error, then the grid is refetched to reconcile).
 */
export const useSetRowInterviewer = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({
			processId,
			interviewId,
			interviewerUserId,
		}: {
			processId: string;
			interviewId: string | null;
			interviewerUserId: string | null;
			interviewerName: string | null;
		}) => {
			// No interview yet (candidate added without one) → CREATE the process's first interview with this
			// interviewer; otherwise PATCH the existing one. The grid refetch on success fills in interview_id.
			if (interviewId) await backendService.updateHubInterview(processId, interviewId, { interviewerUserId });
			else if (interviewerUserId) await backendService.createHubInterview(processId, { interviewerUserId });
		},
		throwOnError: false,
		onMutate: async ({ processId, interviewerUserId, interviewerName }) => {
			track("hiring_hub_row_interviewer_set_start", { process_id: processId });
			await queryClient.cancelQueries({ queryKey: [HUB_CANDIDATES_KEY] });
			const previous = queryClient.getQueriesData<HubCandidatesResponse>({ queryKey: [HUB_CANDIDATES_KEY] });
			queryClient.setQueriesData<HubCandidatesResponse>({ queryKey: [HUB_CANDIDATES_KEY] }, (old) =>
				old
					? {
							...old,
							candidates: old.candidates.map((row) =>
								row.process_id === processId ? { ...row, interviewer_user_id: interviewerUserId, interviewer_name: interviewerName } : row,
							),
						}
					: old,
			);
			return { previous };
		},
		onError: (error, { processId }, context) => {
			for (const [key, data] of context?.previous ?? []) {
				queryClient.setQueryData(key, data);
			}
			trackError("hiring_hub_row_interviewer_set_error", { process_id: processId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
		onSuccess: (_data, { processId }) => {
			track("hiring_hub_row_interviewer_set_success", { process_id: processId });
		},
		onSettled: () => {
			invalidateGrid(queryClient);
		},
	});
};

/**
 * Manual override of a candidate's interview ATTENDANCE status (Pending / At Risk / Confirmed / Reschedule
 * / Cancelled) from the profile interview dialog — restoring the v0 capability the Hub had dropped. Records
 * the status only and refreshes the profile + grid so the chip updates immediately.
 */
export const useSetHubInterviewStatus = (candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		// DEFAULT: sendMessage=false — a Hub attendance edit records the status without re-texting the
		// candidate. The v0 chat dialog offered an opt-in to resend the bot's reply; that opt-in is
		// intentionally not surfaced in the Hub interview dialog (attendance is recorded, not messaged).
		mutationFn: async (status: string) => {
			await backendService.setCandidateStatus(candidateId, status, false);
		},
		throwOnError: false,
		onMutate: (status) => {
			track("hiring_hub_interview_status_set_start", { candidate_id: candidateId, status });
		},
		onSuccess: (_data, status) => {
			track("hiring_hub_interview_status_set_success", { candidate_id: candidateId, status });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.interview.status_saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_interview_status_set_error", { candidate_id: candidateId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

/** Manual override of a cancelled candidate's cancellation reason (null clears it). Refreshes profile + grid. */
export const useSetHubInterviewCancelReason = (candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (cancelReason: string | null) => {
			await backendService.setCandidateCancelReason(candidateId, cancelReason);
		},
		throwOnError: false,
		onMutate: (cancelReason) => {
			track("hiring_hub_interview_cancel_reason_set_start", { candidate_id: candidateId, cancel_reason: cancelReason });
		},
		onSuccess: (_data, cancelReason) => {
			track("hiring_hub_interview_cancel_reason_set_success", { candidate_id: candidateId, cancel_reason: cancelReason });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.interview.cancel_reason_saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_interview_cancel_reason_set_error", { candidate_id: candidateId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

/** Two dates are equal when both are absent or fall on the same calendar day. */
const sameDate = (a: DateTime | null, b: DateTime | null): boolean => {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return a.toISODate() === b.toISODate();
};

/** Serialize a date-only DateTime to the `"yyyy-MM-dd"` the PATCH routes expect (null when unset). */

/** Normalize a bare time string to `"HH:mm"` for comparison ("14:30:00" and "14:30" are equal). */
const normalizeTime = (time: string | null): string | null => {
	if (!time) return null;
	const parsed = DateTime.fromISO(time);
	return parsed.isValid ? parsed.toFormat("HH:mm") : time;
};

/**
 * Route ONE committed inline edit to its backend endpoint. Single-cell edit mode means exactly one logical
 * field differs; each branch returns the PATCH promise (or throws its own validation error). Kept separate
 * from the hook so the field routing reads top-to-bottom. Returns `null` when nothing meaningful changed.
 */
const commitFieldChange = async (newRow: HubCandidateRow, oldRow: HubCandidateRow, t: TFunction): Promise<{ field: string } | null> => {
	// NOTE: department and phone are READ-ONLY this version (they're the import's match keys — an in-app
	// change would risk a re-import duplicate), so they never diff here. The backend PATCH still accepts
	// `departmentId` / `phone` for when editing is enabled after the customer moves fully to the app.
	if (newRow.candidate_name.trim() !== oldRow.candidate_name.trim()) {
		const trimmed = newRow.candidate_name.trim();
		const [firstName, ...rest] = trimmed.split(/\s+/);
		if (!firstName) throw new Error(t("hiring_hub.add_candidate.required"));
		await backendService.updateHubCandidate(newRow.candidate_id, { firstName, lastName: rest.length ? rest.join(" ") : null });
		return { field: "name" };
	}
	if ((newRow.email ?? "") !== (oldRow.email ?? "")) {
		await backendService.updateHubCandidate(newRow.candidate_id, { email: newRow.email?.trim() || null });
		return { field: "email" };
	}
	if ((newRow.position ?? "") !== (oldRow.position ?? "")) {
		await backendService.updateHubProcess(newRow.process_id, { position: newRow.position?.trim() || null });
		return { field: "position" };
	}
	if (newRow.wage !== oldRow.wage) {
		if (newRow.wage != null && newRow.wage < 0) throw new Error(t("hiring_hub.grid.inline.wage_invalid"));
		await backendService.updateHubProcess(newRow.process_id, { wage: newRow.wage });
		return { field: "wage" };
	}
	if (newRow.shift !== oldRow.shift) {
		await backendService.updateHubProcess(newRow.process_id, { shift: (newRow.shift as InterviewShift) || null });
		return { field: "shift" };
	}
	if (!sameDate(newRow.applied_date, oldRow.applied_date)) {
		await backendService.updateHubProcess(newRow.process_id, { appliedDate: toApiDate(newRow.applied_date) });
		return { field: "applied_date" };
	}
	if (!sameDate(newRow.hire_date, oldRow.hire_date)) {
		await backendService.updateHubProcess(newRow.process_id, { hireDate: toApiDate(newRow.hire_date) });
		// Setting a hire date marks the candidate HIRED (promote); clearing it leaves the stage as-is.
		if (newRow.hire_date?.isValid && newRow.pipeline_status !== CandidatePipelineStatus.HIRED) {
			await backendService.setHubPipelineStatus(newRow.process_id, { pipelineStatus: CandidatePipelineStatus.HIRED });
			newRow.pipeline_status = CandidatePipelineStatus.HIRED; // reflect it in the optimistic row so the Status cell updates
		}
		return { field: "hire_date" };
	}
	if ((newRow.notes ?? "") !== (oldRow.notes ?? "")) {
		await backendService.updateHubProcess(newRow.process_id, { notes: newRow.notes?.trim() || null });
		return { field: "notes" };
	}
	if ((newRow.disqualification_reason ?? "") !== (oldRow.disqualification_reason ?? "")) {
		await backendService.updateHubProcess(newRow.process_id, { disqualificationReason: newRow.disqualification_reason?.trim() || null });
		return { field: "disqualification_reason" };
	}
	if ((newRow.never_started_reason ?? "") !== (oldRow.never_started_reason ?? "")) {
		await backendService.updateHubProcess(newRow.process_id, { neverStartedReason: newRow.never_started_reason?.trim() || null });
		return { field: "never_started_reason" };
	}
	if (!sameDate(newRow.background_date, oldRow.background_date)) {
		await backendService.updateHubProcess(newRow.process_id, { backgroundDate: toApiDate(newRow.background_date) });
		return { field: "background_date" };
	}
	if (!sameDate(newRow.onboarding_date, oldRow.onboarding_date)) {
		await backendService.updateHubProcess(newRow.process_id, { onboardingDate: toApiDate(newRow.onboarding_date) });
		return { field: "onboarding_date" };
	}
	if (!sameDate(newRow.orientation_date, oldRow.orientation_date)) {
		await backendService.updateHubProcess(newRow.process_id, { orientationDate: toApiDate(newRow.orientation_date) });
		return { field: "orientation_date" };
	}
	// Interview date/time edit the row's most-recent interview — or CREATE the process's first interview when
	// it has none yet (a candidate added without an interview), so date/time are settable inline. Creating on
	// an empty→empty clear is a no-op (guarded by the value check). The grid refetch then fills interview_id.
	if (!sameDate(newRow.interview_date, oldRow.interview_date)) {
		if (newRow.interview_id) {
			await backendService.updateHubInterview(newRow.process_id, newRow.interview_id, { interviewDate: toApiDate(newRow.interview_date) });
		} else if (newRow.interview_date) {
			await backendService.createHubInterview(newRow.process_id, { interviewDate: toApiDate(newRow.interview_date) });
		}
		return { field: "interview_date" };
	}
	if (normalizeTime(newRow.interview_time) !== normalizeTime(oldRow.interview_time)) {
		if (newRow.interview_id) {
			await backendService.updateHubInterview(newRow.process_id, newRow.interview_id, { interviewTime: normalizeTime(newRow.interview_time) });
		} else if (normalizeTime(newRow.interview_time)) {
			await backendService.createHubInterview(newRow.process_id, { interviewTime: normalizeTime(newRow.interview_time) });
		}
		return { field: "interview_time" };
	}
	// Pipeline status commits directly for EVERY stage — reasons are optional (the two reason-carrying
	// stages, Disqualified / Accepted-never-started, are still refinable from the profile's guided dialog).
	if (newRow.pipeline_status !== oldRow.pipeline_status) {
		await backendService.setHubPipelineStatus(newRow.process_id, { pipelineStatus: newRow.pipeline_status });
		return { field: "pipeline_status" };
	}
	return null;
};

/**
 * Commit an inline grid-cell edit. SystemDataGrid calls this with the edited row; `commitFieldChange` diffs
 * it against the old row and PATCHes the matching endpoint (process / candidate / interview / pipeline
 * status). Every data column is editable — department, name, email, phone, position, wage, shift, applied /
 * hire dates, notes, interview date/time, and pipeline status. Returning `oldRow` reverts the cell (MUI
 * never sees a rejection); on success the grid refetches.
 */
export const useHubGridRowUpdate = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useCallback(
		async (newRow: HubCandidateRow, oldRow: HubCandidateRow): Promise<HubCandidateRow> => {
			try {
				const result = await commitFieldChange(newRow, oldRow, t);
				if (!result) return newRow;
				track("hiring_hub_grid_inline_edit_success", { field: result.field, process_id: newRow.process_id });
				invalidateGrid(queryClient);
				showSnackbar(t("hiring_hub.profile.saved"), "success");
				return newRow;
			} catch (error) {
				trackError("hiring_hub_grid_inline_edit_error", { process_id: newRow.process_id, error_message: (error as Error)?.message });
				showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
				return oldRow;
			}
		},
		[queryClient, t, showSnackbar, track, trackError],
	);
};

// --------------------------------------------------------------------------- //
// Interviewer management
// --------------------------------------------------------------------------- //
/**
 * Grant a user the interviewer role across ONE OR MORE departments in a single Add-Interviewer submit (one
 * PATCH per chosen membership). Invalidates every affected department's interviewer list plus the
 * Interviewers-tab department dropdown so the new interviewer/department shows immediately.
 */
export const useAddHubInterviewer = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ userId, departmentIds }: { userId: string; departmentIds: string[] }) => {
			await Promise.all(departmentIds.map((departmentId) => backendService.addHubInterviewer(departmentId, { userId })));
		},
		throwOnError: false,
		onMutate: ({ departmentIds }) => {
			track("hiring_hub_interviewer_add_start", { department_count: departmentIds.length });
		},
		onSuccess: (_data, { userId, departmentIds }) => {
			track("hiring_hub_interviewer_add_success", { department_count: departmentIds.length });
			for (const departmentId of departmentIds) void queryClient.invalidateQueries({ queryKey: interviewersKey(departmentId) });
			invalidateInterviewerViews(queryClient, userId);
			showSnackbar(t("hiring_hub.interviewers.added"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_interviewer_add_error", { error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

/** Revoke the interviewer role for one (user, department) — the department is passed per call so the roster's
 * mixed-department cards can each remove against their own department. */
export const useRemoveHubInterviewer = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ userId, departmentId }: { userId: string; departmentId: string }) => {
			await backendService.removeHubInterviewer(departmentId, userId);
		},
		throwOnError: false,
		onMutate: ({ departmentId }) => {
			track("hiring_hub_interviewer_remove_start", { department_id: departmentId });
		},
		onSuccess: (_data, { userId, departmentId }) => {
			track("hiring_hub_interviewer_remove_success", { department_id: departmentId });
			void queryClient.invalidateQueries({ queryKey: interviewersKey(departmentId) });
			invalidateInterviewerViews(queryClient, userId);
			showSnackbar(t("hiring_hub.interviewers.removed"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_interviewer_remove_error", { error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

/** Apply an Edit-Interviewer-Departments save in ONE transaction (all-or-nothing) — grant in `addDepartmentIds`,
 *  revoke in `removeDepartmentIds`. Replaces the per-department add/remove loop so a failure can't leave a
 *  partially-applied set (#27). Throws on error so the modal can keep itself open. */
export const useSetInterviewerDepartments = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ userId, addDepartmentIds, removeDepartmentIds }: { userId: string; addDepartmentIds: string[]; removeDepartmentIds: string[] }) => {
			await backendService.setInterviewerDepartments(userId, addDepartmentIds, removeDepartmentIds);
		},
		throwOnError: false,
		onMutate: ({ addDepartmentIds, removeDepartmentIds }) => {
			track("hiring_hub_interviewer_set_departments_start", { add_count: addDepartmentIds.length, remove_count: removeDepartmentIds.length });
		},
		onSuccess: (_data, { userId, addDepartmentIds, removeDepartmentIds }) => {
			track("hiring_hub_interviewer_set_departments_success", { add_count: addDepartmentIds.length, remove_count: removeDepartmentIds.length });
			for (const departmentId of [...addDepartmentIds, ...removeDepartmentIds]) void queryClient.invalidateQueries({ queryKey: interviewersKey(departmentId) });
			invalidateInterviewerViews(queryClient, userId);
			showSnackbar(t("hiring_hub.interviewers.saved"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_interviewer_set_departments_error", { error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

// --------------------------------------------------------------------------- //
// Documents
// --------------------------------------------------------------------------- //
export const useUploadHubDocument = (candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ docType, file }: { docType: HubDocType; file: File }) => {
			await backendService.uploadHubDocument(candidateId, docType, file);
		},
		throwOnError: false,
		onMutate: ({ docType }) => {
			track("hiring_hub_document_upload_start", { candidate_id: candidateId, doc_type: docType });
		},
		onSuccess: (_data, { docType }) => {
			track("hiring_hub_document_upload_success", { candidate_id: candidateId, doc_type: docType });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			// Refresh the grid too so a filename-less resume/license cell flips to the uploaded file name.
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.documents.uploaded"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_document_upload_error", { candidate_id: candidateId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.document_upload"), "error");
		},
	});
};

export const useDeleteHubDocument = (candidateId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (docType: HubDocType) => {
			await backendService.deleteHubDocument(candidateId, docType);
		},
		throwOnError: false,
		onMutate: (docType) => {
			track("hiring_hub_document_delete_start", { candidate_id: candidateId, doc_type: docType });
		},
		onSuccess: (_data, docType) => {
			track("hiring_hub_document_delete_success", { candidate_id: candidateId, doc_type: docType });
			void queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
			invalidateGrid(queryClient);
			showSnackbar(t("hiring_hub.documents.deleted"), "success");
		},
		onError: (error) => {
			trackError("hiring_hub_document_delete_error", { candidate_id: candidateId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.save"), "error");
		},
	});
};

/** Fetch a short-lived download URL for a document and open it. Returns the async trigger + pending flag. */
export const useDownloadHubDocument = (candidateId: string) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	const mutation = useMutation({
		mutationFn: async (docType: HubDocType) => (await backendService.getHubDocumentDownloadUrl(candidateId, docType)).data.url,
		throwOnError: false,
		onMutate: (docType) => {
			track("hiring_hub_document_download_start", { candidate_id: candidateId, doc_type: docType });
		},
		onSuccess: (url: string) => {
			// A short-lived pre-signed S3 URL — open in a new tab. May be popup-blocked if not triggered by
			// a direct click, so keep the download button's onClick calling this synchronously.
			window.open(url, "_blank", "noopener,noreferrer");
		},
		onError: (error) => {
			trackError("hiring_hub_document_download_error", { candidate_id: candidateId, error_message: (error as Error)?.message });
			showSnackbar(t(hubErrorKey(error) ?? "hiring_hub.error.document_download"), "error");
		},
	});

	return mutation;
};
